export default function ModuleSummary() {
  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-br from-[#1A3A2E] to-[#0D1F17] rounded-2xl p-8 text-white">
        <div className="flex items-center gap-3 mb-6">
          <i className="ri-file-list-3-line text-3xl text-[#39FF14]"></i>
          <h2 className="text-3xl font-bold">Resumen Ejecutivo del Módulo 2</h2>
        </div>
        <p className="text-gray-200 text-lg leading-relaxed">
          Dominar la fisiología del ejercicio te permite entender POR QUÉ funciona un programa de entrenamiento, no solo seguir instrucciones. Este módulo te da las herramientas para tomar decisiones inteligentes sobre sistemas energéticos, reclutamiento muscular, fatiga y recuperación.
        </p>
      </div>

      <div className="bg-white rounded-2xl p-8 border border-gray-200">
        <h3 className="text-2xl font-bold text-black mb-6">Conceptos Dominados</h3>
        <div className="space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#39FF14] rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-flashlight-line text-black text-xl"></i>
            </div>
            <div>
              <h4 className="font-bold text-black mb-2">Sistemas Energéticos</h4>
              <p className="text-gray-700 leading-relaxed">
                Sabes que el ATP-PC domina esfuerzos de 0-10s (fuerza máxima), el glucolítico de 10s-3min (hipertrofia, HIIT), y el oxidativo en esfuerzos prolongados (resistencia). Ajustas los descansos según el sistema que entrenas: 3-5min para fuerza, 60-120s para hipertrofia, 30-60s para resistencia.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#39FF14] rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-pulse-line text-black text-xl"></i>
            </div>
            <div>
              <h4 className="font-bold text-black mb-2">Reclutamiento de Unidades Motoras</h4>
              <p className="text-gray-700 leading-relaxed">
                Entiendes el Principio del Tamaño: las fibras tipo I se reclutan primero, las tipo II (las que más crecen) solo con alta intensidad, velocidad o fatiga. Por eso tanto el trabajo pesado (5 reps al 85%) como las series al fallo con cargas moderadas (12 reps al 75%) generan hipertrofia: ambos reclutan fibras tipo II.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#39FF14] rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-battery-low-line text-black text-xl"></i>
            </div>
            <div>
              <h4 className="font-bold text-black mb-2">Fatiga Central vs Periférica</h4>
              <p className="text-gray-700 leading-relaxed">
                Distingues entre fatiga muscular local (periférica: metabolitos, depleción energética) y fatiga del sistema nervioso (central: desmotivación, todo se siente pesado). La fatiga central requiere más recuperación (24-72h, sueño, deload) que la periférica (12-48h, nutrición, descanso).
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#39FF14] rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-line-chart-line text-black text-xl"></i>
            </div>
            <div>
              <h4 className="font-bold text-black mb-2">Mecanismos de Hipertrofia</h4>
              <p className="text-gray-700 leading-relaxed">
                Priorizas la tensión mecánica (cargas 70-85%, control excéntrico, ROM completo, cerca del fallo) como estímulo primario. Usas el estrés metabólico (12-20 reps, descansos cortos, pump) como complemento. No buscas daño muscular activamente: el DOMS no correlaciona con crecimiento.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#39FF14] rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-refresh-line text-black text-xl"></i>
            </div>
            <div>
              <h4 className="font-bold text-black mb-2">Recuperación y Supercompensación</h4>
              <p className="text-gray-700 leading-relaxed">
                Comprendes que el progreso ocurre durante la recuperación, no durante el entrenamiento. Programas el volumen entre MEV (mínimo efectivo) y MRV (máximo recuperable), ajustas la frecuencia según tu nivel (principiantes: alta frecuencia/bajo volumen, avanzados: baja frecuencia/alto volumen), y programas deloads cada 4-6 semanas.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#F5F2EB] rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-black mb-6">Aplicación Práctica Inmediata</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-5 border border-gray-200">
            <h4 className="font-bold text-black mb-2">Para Fuerza Máxima</h4>
            <p className="text-sm text-gray-700 leading-relaxed">
              1-5 reps, &gt;85% 1RM, descansos 3-5min. Sistema ATP-PC, reclutamiento máximo, fatiga principalmente central. Frecuencia: 2-3x/semana por patrón.
            </p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200">
            <h4 className="font-bold text-black mb-2">Para Hipertrofia</h4>
            <p className="text-sm text-gray-700 leading-relaxed">
              6-12 reps, 70-85% 1RM, descansos 60-120s. Sistema glucolítico, tensión mecánica + estrés metabólico, fatiga mixta. Frecuencia: 2x/semana por músculo.
            </p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200">
            <h4 className="font-bold text-black mb-2">Para Resistencia Muscular</h4>
            <p className="text-sm text-gray-700 leading-relaxed">
              15-20+ reps, &lt;70% 1RM, descansos 30-60s. Transición glucolítico-oxidativo, estrés metabólico alto, fatiga periférica. Frecuencia: 2-3x/semana.
            </p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200">
            <h4 className="font-bold text-black mb-2">Señales de Sobreentrenamiento</h4>
            <p className="text-sm text-gray-700 leading-relaxed">
              Fatiga central crónica, desmotivación, sueño alterado, estancamiento o pérdida de fuerza. Solución: deload inmediato (reduce volumen 50% durante 1 semana).
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-8 border-2 border-[#39FF14]">
        <h3 className="text-2xl font-bold text-black mb-4">Siguiente Paso</h3>
        <p className="text-gray-700 leading-relaxed mb-6">
          Ahora que dominas la fisiología del ejercicio, estás listo para el <strong>Módulo 3: Metodologías de Entrenamiento</strong>, donde aprenderás a aplicar estos principios en sistemas estructurados (Weider, Heavy Duty, HIT, periodización) y elegir el método óptimo según tu objetivo y nivel.
        </p>
        <div className="flex items-center gap-3 text-[#1A3A2E]">
          <i className="ri-arrow-right-line text-xl"></i>
          <span className="font-medium">Continúa al Módulo 3 cuando estés listo</span>
        </div>
      </div>
    </div>
  );
}